import {
  SCORING_CHECKLIST_KEY,
  type ScoringChecklist,
  type Y,
} from '@piccolohealth/pbs-common';
import { uuid } from '@piccolohealth/util';
import React from 'react';

export const DEFAULT_SYSTEM_PROMPT = `
You are a helpful assistant that scores participant documents.

You will be given a participant document and a checklist of criteria to score the document against.

You will need to score the document based on the criteria in the checklist.

You will need to return a score for the document based on the criteria in the checklist.
`;

interface UseScoringChecklistOptions {
  yDoc: Y.Doc;
}

const getChecklist = (yDoc: Y.Doc): ScoringChecklist | null => {
  const map = yDoc.getMap(SCORING_CHECKLIST_KEY);

  if (map.size === 0) {
    return null;
  }

  return (map.get('value') as ScoringChecklist) ?? null;
};

export const useScoringChecklist = (options: UseScoringChecklistOptions) => {
  const { yDoc } = options;

  const [checklist, setChecklist] = React.useState<ScoringChecklist | null>(() =>
    getChecklist(yDoc),
  );

  const updateChecklist = React.useCallback(
    (checklist: ScoringChecklist) => {
      yDoc.transact(() => {
        yDoc.getMap(SCORING_CHECKLIST_KEY).set('value', checklist);
      });
    },
    [yDoc],
  );

  const addChecklist = React.useCallback(() => {
    if (checklist) {
      return;
    }

    const newChecklist: ScoringChecklist = {
      id: uuid(),
      systemPrompt: DEFAULT_SYSTEM_PROMPT,
      criteriaLabels: [],
      categories: [],
    };

    updateChecklist(newChecklist);
  }, [checklist, updateChecklist]);

  const deleteChecklist = React.useCallback(() => {
    yDoc.transact(() => {
      yDoc.getMap(SCORING_CHECKLIST_KEY).delete('value');
    });
  }, [yDoc]);

  // Observe the Yjs map directly so the UI reacts to mutations from any source
  // (local form edits, collaborative changes, or programmatic add/delete).
  React.useEffect(() => {
    const map = yDoc.getMap(SCORING_CHECKLIST_KEY);

    setChecklist(getChecklist(yDoc));

    const observer = () => {
      setChecklist(getChecklist(yDoc));
    };

    map.observe(observer);
    return () => map.unobserve(observer);
  }, [yDoc]);

  return {
    checklist,
    addChecklist,
    updateChecklist,
    deleteChecklist,
  };
};
